<script setup lang="ts">
import { computed, watch } from 'vue'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  getInitials,
  getRawAvatarUrl,
  isPlaceholderAvatarUrl,
  isSharedDefaultAvatarUrl,
  trackAvatarUrl,
} from '@/lib/avatar'

// ─── Component identity ───────────────────────────────────────────────────────

defineOptions({
  name: 'CAvatar',
  // Prevent Vue from forwarding unknown attrs (id, style, data-*) onto the
  // root <Avatar> element — we handle class manually via the prop below.
  inheritAttrs: false,
})

// ─── Props ────────────────────────────────────────────────────────────────────

const props = defineProps({
  /** User object. Accepts any shape; fields accessed via optional chaining. */
  user: {
    type: Object,
    default: null,
  },
  /**
   * Extra CSS classes forwarded to the root <Avatar>.
   * Named `avatarClass` to avoid shadowing the JS reserved word `class`.
   * Bind with  :avatar-class="..."  or  avatarClass="..."  at the call-site.
   */
  avatarClass: {
    type: String,
    default: '',
  },
})

// ─── Derived state ────────────────────────────────────────────────────────────

const displayName = computed(
  () => props.user?.full_name ?? props.user?.name ?? '',
)

const initials = computed(() => getInitials(displayName.value) || '?')

const rawSrc = computed(() => getRawAvatarUrl(props.user))

// Track which users share which URL so isSharedDefaultAvatarUrl stays current.
// `watch` with { immediate: true } is explicit about which dep we care about,
// unlike watchEffect which re-runs on every reactive read inside the callback.
watch(
  () => [rawSrc.value, props.user?.id],
  ([src, id]) => {
    if (src && id != null) trackAvatarUrl(src, id)
  },
  { immediate: true },
)

/**
 * Resolved image URL — null when the user has no real custom upload.
 * Filters out:
 *   • API placeholder / Gravatar silhouettes
 *   • The same URL shared by 2+ users (system default detected at runtime)
 */
const avatarSrc = computed(() => {
  const src = rawSrc.value
  if (!src) return null
  if (isPlaceholderAvatarUrl(src)) return null
  if (isSharedDefaultAvatarUrl(src)) return null
  return src
})
</script>

<template>
  <Avatar
    :class="cn('rounded-full overflow-hidden size-9 border shrink-0', avatarClass)"
  >
    <AvatarImage
      v-if="avatarSrc"
      :src="avatarSrc"
      :alt="displayName"
    />
    <AvatarFallback class="bg-muted text-muted-foreground font-medium">
      {{ initials }}
    </AvatarFallback>
  </Avatar>
</template>